import React, { useState, createElement } from 'react';
import { Box, Typography, Button, Card, CardContent, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Divider, LinearProgress, Alert } from '@mui/material';
import { Icon } from '@iconify/react';
import { useToast } from './ui/ToastProvider';
interface ReversalRecord {
  originalTxnDate: string;
  txnRail: '1link' | 'RAAST' | 'RTGS';
  partner: string;
  txnAmount: string;
  cbsReference: string;
  txnReference: string;
  receiverIban: string;
  receiverName: string;
  validationStatus: 'valid' | 'invalid' | 'warning';
  validationMessage?: string;
  reversalStatus?: 'pending' | 'success' | 'failed' | 'not_found';
  reversalMessage?: string;
}
interface ReversalUploadCardProps {
  onClose: () => void;
}
const ReversalUploadCard: React.FC<ReversalUploadCardProps> = ({
  onClose
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'initial' | 'validating' | 'validated' | 'processing' | 'completed'>('initial');
  const [records, setRecords] = useState<ReversalRecord[]>([]);
  const [processingProgress, setProcessingProgress] = useState(0);
  const {
    showToast
  } = useToast();
  const validateTxnReference = (rail: string, reference: string): {
    valid: boolean;
    message?: string;
  } => {
    if (rail === '1link') {
      const isValid = /^\d{6}$/.test(reference);
      return {
        valid: isValid,
        message: isValid ? undefined : 'STAN must be exactly 6 digits'
      };
    } else if (rail === 'RAAST') {
      const isValid = /^[a-zA-Z0-9]{3,35}$/.test(reference);
      return {
        valid: isValid,
        message: isValid ? undefined : 'MessageId must be 3-35 alphanumeric characters'
      };
    } else if (rail === 'RTGS') {
      const isValid = reference.length > 0;
      return {
        valid: isValid,
        message: isValid ? undefined : 'RTGS uniqueId is required'
      };
    }
    return {
      valid: false,
      message: 'Invalid transaction rail'
    };
  };
  const validateRecord = (record: Omit<ReversalRecord, 'validationStatus' | 'validationMessage'>): ReversalRecord => {
    const errors: string[] = [];
    // Validate required fields
    if (!record.originalTxnDate) errors.push('Original Txn Date is required');
    if (!record.txnRail) errors.push('Txn Rail is required');
    if (!record.partner) errors.push('Partner is required');
    if (!record.txnAmount) errors.push('Txn Amount is required');
    if (!record.txnReference) errors.push('Txn Reference is required');
    if (!record.receiverIban) errors.push('Receiver IBAN is required');
    if (!record.receiverName) errors.push('Receiver Name is required');
    // Validate txn rail
    if (record.txnRail && !['1link', 'RAAST', 'RTGS'].includes(record.txnRail)) {
      errors.push('Txn Rail must be 1link, RAAST, or RTGS');
    }
    // Validate txn reference based on rail
    if (record.txnRail && record.txnReference) {
      const refValidation = validateTxnReference(record.txnRail, record.txnReference);
      if (!refValidation.valid) {
        errors.push(refValidation.message!);
      }
    }
    // Validate IBAN format - Pakistan IBAN starts with PK followed by alphanumeric characters
    if (record.receiverIban) {
      const iban = record.receiverIban.replace(/\s/g, '').toUpperCase(); // Remove spaces and uppercase
      if (!/^PK[0-9A-Z]+$/.test(iban)) {
        errors.push('Invalid IBAN format (must start with PK followed by alphanumeric characters)');
      }
    }
    // Validate amount
    if (record.txnAmount && isNaN(Number(record.txnAmount))) {
      errors.push('Txn Amount must be a valid number');
    }
    return {
      ...record,
      validationStatus: errors.length === 0 ? 'valid' : 'invalid',
      validationMessage: errors.join('; ')
    };
  };
  const parseCSVFile = (file: File): Promise<ReversalRecord[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim());
          // Skip header row
          const dataLines = lines.slice(1);
          const records = dataLines.map(line => {
            // Handle CSV with quoted values
            const values: string[] = [];
            let current = '';
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                values.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            values.push(current.trim());
            const record = {
              originalTxnDate: values[0] || '',
              txnRail: (values[1] || '') as '1link' | 'RAAST' | 'RTGS',
              partner: values[2] || '',
              txnAmount: values[3] || '',
              cbsReference: values[4] || '',
              txnReference: values[5] || '',
              receiverIban: values[6] || '',
              receiverName: values[7] || ''
            };
            return validateRecord(record);
          });
          resolve(records);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      await processFile(droppedFile);
    }
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      await processFile(selectedFile);
    }
  };
  const processFile = async (file: File) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      showToast('Please upload a CSV or Excel file', 'error');
      return;
    }
    setFile(file);
    setUploadStatus('validating');
    try {
      const parsedRecords = await parseCSVFile(file);
      setRecords(parsedRecords);
      const validCount = parsedRecords.filter(r => r.validationStatus === 'valid').length;
      const invalidCount = parsedRecords.filter(r => r.validationStatus === 'invalid').length;
      if (validCount === 0) {
        setUploadStatus('validated');
        showToast(`Validation complete: ${invalidCount} invalid records. Please fix errors and re-upload.`, 'error');
        return;
      }
      showToast(`Validation complete: ${validCount} valid, ${invalidCount} invalid`, 'success');
      // Automatically process reversals for valid records
      setUploadStatus('processing');
      setProcessingProgress(0);
      const validRecords = parsedRecords.filter(r => r.validationStatus === 'valid');
      const processedRecords: ReversalRecord[] = [];
      for (let i = 0; i < validRecords.length; i++) {
        const processed = await simulateReversal(validRecords[i]);
        processedRecords.push(processed);
        setProcessingProgress((i + 1) / validRecords.length * 100);
      }
      // Update records with reversal results
      const updatedRecords = parsedRecords.map(record => {
        const processed = processedRecords.find(p => p.txnReference === record.txnReference && p.receiverIban === record.receiverIban);
        return processed || record;
      });
      setRecords(updatedRecords);
      setUploadStatus('completed');
      showToast('Reversal processing completed', 'success');
    } catch (error) {
      showToast('Failed to parse file', 'error');
      setUploadStatus('initial');
    }
  };
  const simulateReversal = (record: ReversalRecord): Promise<ReversalRecord> => {
    return new Promise(resolve => {
      setTimeout(() => {
        // Simulate different outcomes
        const random = Math.random();
        if (random < 0.6) {
          resolve({
            ...record,
            reversalStatus: 'success',
            reversalMessage: 'Reversed successfully'
          });
        } else if (random < 0.8) {
          resolve({
            ...record,
            reversalStatus: 'not_found',
            reversalMessage: 'No txn found against this data'
          });
        } else {
          resolve({
            ...record,
            reversalStatus: 'failed',
            reversalMessage: 'Reversal failed'
          });
        }
      }, 500);
    });
  };
  const handleProcessReversals = async () => {
    const validRecords = records.filter(r => r.validationStatus === 'valid');
    if (validRecords.length === 0) {
      showToast('No valid records to process', 'error');
      return;
    }
    setUploadStatus('processing');
    setProcessingProgress(0);
    const processedRecords: ReversalRecord[] = [];
    for (let i = 0; i < validRecords.length; i++) {
      const processed = await simulateReversal(validRecords[i]);
      processedRecords.push(processed);
      setProcessingProgress((i + 1) / validRecords.length * 100);
    }
    // Update records with reversal results
    const updatedRecords = records.map(record => {
      const processed = processedRecords.find(p => p.txnReference === record.txnReference && p.receiverIban === record.receiverIban);
      return processed || record;
    });
    setRecords(updatedRecords);
    setUploadStatus('completed');
    showToast('Reversal processing completed', 'success');
  };
  const handleDownloadResults = () => {
    const headers = ['Original Txn Date', 'Txn Rail', 'Partner', 'Txn Amount', 'CBS Reference', 'Txn Reference', 'Receiver IBAN', 'Receiver Name', 'Status', 'Message'];
    const rows = records.map(r => [r.originalTxnDate, r.txnRail, r.partner, r.txnAmount, r.cbsReference, r.txnReference, r.receiverIban, r.receiverName, r.reversalStatus || r.validationStatus, r.reversalMessage || r.validationMessage || '']);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reversal_results_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  const validCount = records.filter(r => r.validationStatus === 'valid').length;
  const invalidCount = records.filter(r => r.validationStatus === 'invalid').length;
  const successCount = records.filter(r => r.reversalStatus === 'success').length;
  const failedCount = records.filter(r => r.reversalStatus === 'failed').length;
  const notFoundCount = records.filter(r => r.reversalStatus === 'not_found').length;
  return <Card elevation={0} sx={{
    borderRadius: 2,
    border: '1px solid #E5E7EB'
  }} data-id="element-1713">
      <CardContent data-id="element-1714">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }} data-id="element-1715">
          <Typography variant="h6" data-id="element-1716">
            {uploadStatus === 'initial' && 'Upload Reversal File'}
            {uploadStatus === 'validating' && 'Validating File...'}
            {uploadStatus === 'validated' && 'Validation Results'}
            {uploadStatus === 'processing' && 'Processing Reversals...'}
            {uploadStatus === 'completed' && 'Reversal Results'}
          </Typography>
          <IconButton onClick={onClose} size="small" data-id="element-1717">
            <Icon icon="lucide:x" width={18} data-id="element-1718" />
          </IconButton>
        </Box>

        {uploadStatus === 'initial' && <Box sx={{
        border: `2px dashed ${isDragging ? '#EF4444' : '#D1D5DB'}`,
        borderRadius: 2,
        p: 4,
        backgroundColor: isDragging ? 'rgba(239, 68, 68, 0.04)' : '#FFFFFF',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => document.getElementById('reversal-file-upload')?.click()} data-id="element-1719">
            <input id="reversal-file-upload" type="file" accept=".csv,.xlsx" onChange={handleFileChange} style={{
          display: 'none'
        }} data-id="element-1720" />
            <Icon icon="lucide:upload" width={48} height={48} color="#EF4444" style={{
          margin: '0 auto 16px'
        }} data-id="element-1721" />
            <Typography variant="h6" gutterBottom data-id="element-1722">
              Drop your reversal file here
            </Typography>
            <Typography variant="body2" color="text.secondary" data-id="element-1723">
              or click to browse (CSV or Excel files)
            </Typography>
          </Box>}

        {uploadStatus === 'validating' && <Box sx={{
        textAlign: 'center',
        py: 4
      }} data-id="element-1724">
            <LinearProgress sx={{
          mb: 2
        }} data-id="element-1725" />
            <Typography variant="body1" color="text.secondary" data-id="element-1726">
              Validating file data...
            </Typography>
          </Box>}

        {(uploadStatus === 'validated' || uploadStatus === 'processing' || uploadStatus === 'completed') && <>
            <Box sx={{
          mb: 3
        }} data-id="element-1727">
              <Typography variant="subtitle2" sx={{
            mb: 1
          }} data-id="element-1728">
                File:{' '}
                <span style={{
              fontWeight: 400
            }} data-id="element-1729">
                  {file?.name}
                </span>
              </Typography>
              <Box sx={{
            display: 'flex',
            gap: 2,
            mt: 2,
            flexWrap: 'wrap'
          }} data-id="element-1730">
                <Chip icon={<Icon icon="lucide:check-circle" width={16} data-id="element-1732" />} label={`${validCount} Valid`} sx={{
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              color: '#10B981',
              '.MuiChip-icon': {
                color: '#10B981'
              }
            }} data-id="element-1731" />
                {invalidCount > 0 && <Chip icon={<Icon icon="lucide:alert-circle" width={16} data-id="element-1734" />} label={`${invalidCount} Invalid`} sx={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#EF4444',
              '.MuiChip-icon': {
                color: '#EF4444'
              }
            }} data-id="element-1733" />}
                {uploadStatus === 'completed' && <>
                    <Chip icon={<Icon icon="lucide:check" width={16} data-id="element-1736" />} label={`${successCount} Reversed`} sx={{
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                color: '#10B981',
                '.MuiChip-icon': {
                  color: '#10B981'
                }
              }} data-id="element-1735" />
                    {failedCount > 0 && <Chip icon={<Icon icon="lucide:x" width={16} data-id="element-1738" />} label={`${failedCount} Failed`} sx={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: '#EF4444',
                '.MuiChip-icon': {
                  color: '#EF4444'
                }
              }} data-id="element-1737" />}
                    {notFoundCount > 0 && <Chip icon={<Icon icon="lucide:search-x" width={16} data-id="element-1740" />} label={`${notFoundCount} Not Found`} sx={{
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                color: '#F59E0B',
                '.MuiChip-icon': {
                  color: '#F59E0B'
                }
              }} data-id="element-1739" />}
                  </>}
              </Box>
            </Box>

            {uploadStatus === 'processing' && <Box sx={{
          mb: 3
        }} data-id="element-1741">
                <LinearProgress variant="determinate" value={processingProgress} data-id="element-1742" />
                <Typography variant="body2" color="text.secondary" sx={{
            mt: 1,
            textAlign: 'center'
          }} data-id="element-1743">
                  Processing reversals... {Math.round(processingProgress)}%
                </Typography>
              </Box>}

            {invalidCount > 0 && uploadStatus === 'validated' && <Alert severity="warning" sx={{
          mb: 3
        }} data-id="element-1744">
                {invalidCount} record(s) have validation errors. Only valid
                records will be processed.
              </Alert>}

            <TableContainer component={Paper} variant="outlined" sx={{
          mb: 3,
          maxHeight: 400
        }} data-id="element-1745">
              <Table size="small" stickyHeader data-id="element-1746">
                <TableHead data-id="element-1747">
                  <TableRow data-id="element-1748">
                    <TableCell data-id="element-1749">Status</TableCell>
                    <TableCell data-id="element-1750">Date</TableCell>
                    <TableCell data-id="element-1751">Rail</TableCell>
                    <TableCell data-id="element-1752">Partner</TableCell>
                    <TableCell data-id="element-1753">Amount</TableCell>
                    <TableCell data-id="element-1754">Reference</TableCell>
                    <TableCell data-id="element-1755">Receiver IBAN</TableCell>
                    <TableCell data-id="element-1756">Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody data-id="element-1757">
                  {records.map((record, index) => <TableRow key={index} sx={{
                backgroundColor: record.validationStatus === 'invalid' || record.reversalStatus === 'failed' ? 'rgba(239, 68, 68, 0.05)' : record.reversalStatus === 'success' ? 'rgba(16, 185, 129, 0.05)' : 'inherit'
              }} data-id="element-1758">
                      <TableCell data-id="element-1759">
                        {record.reversalStatus === 'success' && <Chip label="Success" size="small" sx={{
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    color: '#10B981'
                  }} data-id="element-1760" />}
                        {record.reversalStatus === 'failed' && <Chip label="Failed" size="small" sx={{
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    color: '#EF4444'
                  }} data-id="element-1761" />}
                        {record.reversalStatus === 'not_found' && <Chip label="Not Found" size="small" sx={{
                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                    color: '#F59E0B'
                  }} data-id="element-1762" />}
                        {!record.reversalStatus && record.validationStatus === 'valid' && <Chip label="Valid" size="small" sx={{
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    color: '#10B981'
                  }} data-id="element-1763" />}
                        {!record.reversalStatus && record.validationStatus === 'invalid' && <Chip label="Invalid" size="small" sx={{
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    color: '#EF4444'
                  }} data-id="element-1764" />}
                      </TableCell>
                      <TableCell data-id="element-1765">{record.originalTxnDate}</TableCell>
                      <TableCell data-id="element-1766">
                        <Chip label={record.txnRail} size="small" data-id="element-1767" />
                      </TableCell>
                      <TableCell data-id="element-1768">{record.partner}</TableCell>
                      <TableCell data-id="element-1769">{record.txnAmount}</TableCell>
                      <TableCell sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem'
                }} data-id="element-1770">
                        {record.txnReference}
                      </TableCell>
                      <TableCell sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem'
                }} data-id="element-1771">
                        {record.receiverIban}
                      </TableCell>
                      <TableCell data-id="element-1772">
                        <Typography variant="caption" color={record.validationStatus === 'invalid' || record.reversalStatus === 'failed' ? 'error' : 'text.secondary'} data-id="element-1773">
                          {record.reversalMessage || record.validationMessage || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </TableContainer>
          </>}

        <Divider sx={{
        my: 2
      }} data-id="element-1774" />

        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2
      }} data-id="element-1775">
          <Button variant="outlined" onClick={onClose} data-id="element-1776">
            {uploadStatus === 'completed' ? 'Close' : 'Cancel'}
          </Button>
          <Box sx={{
          display: 'flex',
          gap: 2
        }} data-id="element-1777">
            {uploadStatus === 'completed' && <Button variant="outlined" startIcon={<Icon icon="lucide:download" width={16} data-id="element-1779" />} onClick={handleDownloadResults} data-id="element-1778">
                Download Results
              </Button>}
            {uploadStatus === 'validated' && <Button variant="contained" onClick={handleProcessReversals} disabled={validCount === 0} sx={{
            background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)'
            }
          }} data-id="element-1780">
                Process {validCount} Reversal{validCount !== 1 ? 's' : ''}
              </Button>}
          </Box>
        </Box>
      </CardContent>
    </Card>;
};
export default ReversalUploadCard;